import React from "react";
import PropTypes from "prop-types";
import {
  caseTypes,
  caseTypeDefault,
  stringFieldDefault
} from "components/Utility";
import { connect } from "react-redux";
import { withRouter } from "tools/routerCompat";
import MCard from "components/Shared/MCard.tsx";
import { DATA_TYPE_HYPERLINK, DATA_TYPE_EMAIL, DATA_TYPE_DATE_TIME, DATA_TYPE_MONEY, DATA_TYPE_PERCENT } from "typeConstants/dataTypes";
import { getCaseHoldStatus } from "actions/homeowner/dashboard";
import { CaseFieldLabels } from "tools/caseTypeLabels";
import { CASE_HEADER_ORIGINAL_SURVEY_TYPE_VIEW, STAMP_PHOTO_REQUIREMENTS_LINK_VIEW } from "typeConstants/rights";

class OrderInformation extends React.Component {
  constructor(props) {
    super(props);
    this.getFields = this.getFields.bind(this);
    this.getInsuranceFields = this.getInsuranceFields.bind(this);
    this.getValuationFields = this.getValuationFields.bind(this);
    this.convertToTwoColumns = this.convertToTwoColumns.bind(this);
    this.getControlNumberField = this.getControlNumberField.bind(this);
  }

  componentDidMount() {
    const { getCaseHoldStatus, controlNumber, holdStatus } = this.props;
    if (controlNumber) {
      getCaseHoldStatus(controlNumber);
      return holdStatus;
    }
  }

  getFields() {
    const { caseType, showAsTwoColumn, holdStatus } = this.props;
    let fields = [];
    switch (caseType) {
      case caseTypes.insurance:
        fields = this.getInsuranceFields();
        break;
      case caseTypes.valuation:
        fields = this.getValuationFields();
        break;
    }
    if (this.props.primaryStatus) {
      fields.push({
        key: "primaryStatus",
        value: this.props.primaryStatus,
        label: "Primary Status"
      });
    }
    if (this.props.webStatus) {
      fields.push({
        key: "webStatus",
        value: this.props.webStatus,
        label: "Field Status "
      });
    }
    if (this.props.AppraisalStatus && caseType == caseTypes.valuation) {
      fields.push({
        key: "AppraisalStatus",
        value: this.props.AppraisalStatus,
        label: "Appraisal Status"
      });
    }
    if (this.props.cubiCasaStatus) {
      fields.push({
        key: "CubiCasaStatus",
        value: this.props.cubiCasaStatus,
        label: "CubiCasa Status"
      });
    }
    if (holdStatus?.caseSecondaryStatus) {
      let details = [{
        key: "hold-start-date",
        label: "Start Date",
        value: holdStatus.startDate,
        dataType: DATA_TYPE_DATE_TIME
      }, {
        key: "time-on-hold",
        label: "Days on Hold",
        value: holdStatus.daysOnHold.toFixed(2)
      }, {
        key: "put-on-hold-by",
        label: "Put on Hold by",
        value: holdStatus.userPutOnHold.fullName
      }, {
        key: "put-on-hold-by-email",
        value: holdStatus.userPutOnHold.emailAddress,
        dataType: DATA_TYPE_EMAIL
      }];
      fields.push({
        key: "hold-status",
        label: "Hold Status",
        value: "Yes",
        tooltip: {
          title: "Hold Status Details",
          fields: details
        }
      });
    } else fields.push({
      key: "hold-status",
      label: "Hold Status",
      value: "No",
    });
    return showAsTwoColumn ? this.convertToTwoColumns(fields) : fields;
  }

  getInsuranceFields() {
    const {
      policyOrLoanNumber,
      trackingNumber,
      reportType,
      originalSurveyType,
      customerNumber,
      customerName,
      orderedDate,
      surveyDate,
      returnedDate,
      completedDate,
      customerDueDate,
      orderCodeOut,
      caseType,
      yearBuiltIn,
      user,
      coverageAIn,
      coverageAOut,
      percentITV,
    } = this.props;
    const insuranceFields = [this.getControlNumberField(), {
      key: "policy-number",
      label: CaseFieldLabels.PolicyNumber.getLabel(caseType),
      value: policyOrLoanNumber,
    }, {
      key: "tracking-number",
      label: "Tracking #",
      value: trackingNumber,
    }, {
      key: "report-type",
      label: CaseFieldLabels.SurveyType.getLabel(),
      value: reportType,
    }];
    if (user.rights.includes(CASE_HEADER_ORIGINAL_SURVEY_TYPE_VIEW)) {
      insuranceFields.push({
        key: "original-survey-type",
        label: "Original Survey Type",
        value: originalSurveyType });
    }
    insuranceFields.push({
      key: "customer-name",
      label: CaseFieldLabels.Customer.getLabel(caseType),
      value: `${customerName} (${customerNumber})`,
    });
    if (user.rights.includes(STAMP_PHOTO_REQUIREMENTS_LINK_VIEW)) {
      insuranceFields.push(this.getStampAndPhotoRequirementsField());
    }
    insuranceFields.push({
      key: "ordered-date-column2",
      label: "Ordered",
      value: orderedDate,
    }, {
      key: "customer-due-date-column2",
      label: "Customer Due",
      value: customerDueDate,
    }, {
      key: "year-built",
      label: "Year Built",
      value: yearBuiltIn,
    }, {
      key: "survey-date-column2",
      label: "Surveyed",
      value: surveyDate,
    }, {
      key: "returned-date-column2",
      label: "Returned",
      value: returnedDate
    }, {
      key: "completed-date-column2",
      label: "Completed",
      value: completedDate
    }, {
      key: "coverage-a-in",
      label: "Coverage A In",
      value: coverageAIn,
      dataType: DATA_TYPE_MONEY,
    }, {
      key: "coverage-a-out",
      label: "Coverage A Out",
      value: coverageAOut,
      dataType: DATA_TYPE_MONEY,
    }, {
      key: "percent-to-value",
      label: "Percent to Value",
      value: percentITV,
      dataType: DATA_TYPE_PERCENT,
    });

    if (orderCodeOut) {
      insuranceFields.push(
        {
          key: "order-code-out-column2",
          label: "Stack Code",
          value: orderCodeOut
        }
      );
    }

    return insuranceFields;
  }

  getValuationFields() {
    const {
      policyOrLoanNumber,
      trackingNumber,
      reportType,
      assignmentType,
      customerNumber,
      customerName,
      orderedDate,
      surveyDate,
      returnedDate,
      completedDate,
      customerDueDate,
      user,
      caseType,
      governmentSponsor,
      channel,
    } = this.props;

    const valuationFields = [
      this.getControlNumberField(),
      {
        key: "loan-number",
        label: CaseFieldLabels.PolicyNumber.getLabel(caseType),
        value: policyOrLoanNumber
      },
      {
        key: "tracking-number",
        label: "Tracking #",
        value: trackingNumber,
      },
      {
        key: "report-type",
        label: CaseFieldLabels.SurveyType.getLabel(),
        value: reportType
      },
      {
        key: "lender-name",
        label: CaseFieldLabels.Customer.getLabel(caseType),
        value: `${customerName} (${customerNumber})`,
      }];
      if (user.rights.includes(STAMP_PHOTO_REQUIREMENTS_LINK_VIEW)) {
        valuationFields.push(this.getStampAndPhotoRequirementsField());
      }
      valuationFields.push(
      {
        key: "ordered-date-column2",
        label: "Ordered",
        value: orderedDate
      },
      {
        key: "customer-due-date-column2",
        label: CaseFieldLabels.Customer.getLabel(caseType) + " Due",
        value: user.roles.includes("Appraiser") || user.roles.includes("MuellerAppraiser")
          ? null
          : customerDueDate,
      },
      {
        key: "survey-date-column2",
        label: "Surveyed",
        value: surveyDate
      },
      {
        key: "returned-date-column2",
        label: "Returned",
        value: returnedDate
      },
      {
        key: "completed-date-column2",
        label: "Completed",
        value: completedDate
      });

    if (assignmentType) {
      valuationFields.push({
        key: "assignment-type",
        label: "Assignment Type",
        value: assignmentType,
      });
    }

    if (channel) {
      valuationFields.push({
        key: "channel",
        label: "Channel",
        value: channel,
      });
    }

    if (governmentSponsor) {
      valuationFields.push({
        key: "governmentSponsor",
        label: "GSE",
        value: governmentSponsor,
      });
    }

    return valuationFields;
  }

  getControlNumberField() {
    const { legacyDataEntryPageLink, controlNumber } = this.props;
    if (legacyDataEntryPageLink) {
      return {
        key: "control-number",
        label: "Control #",
        value: { label: controlNumber, onClick: legacyDataEntryPageLink },
        dataType: DATA_TYPE_HYPERLINK
      };
    }
    return {
      key: "control-number",
      label: "Control #",
      value: controlNumber
    };
  }

  getStampAndPhotoRequirementsField() {
    return {
      key: "stamp-and-photo-requirements-link",
      label: "Requirements",
      value: this.props.stampsAndPhotoRequirementsLink,
      dataType: DATA_TYPE_HYPERLINK };
  }

  convertToTwoColumns(fieldsArray) {
    if (fieldsArray && Array.isArray(fieldsArray)) {
      return [
        fieldsArray.filter(field => !field.key.includes("column2")),
        fieldsArray.filter(field => field.key.includes("column2")),
      ];
    }
  }

  render() {
    const { className } = this.props;
    return (
      <MCard
        title="Order Information"
        className={className}
        fields={this.getFields()}
      />
    );
  }
}

const mapStateToProps = (state) => {
  return {
    user: state.user.profile,
    fetchingHoldStatus: state.dashboard.fetchingHoldStatus,
    holdStatus: state.dashboard.holdStatus,
    yearBuiltIn: state.caseForms.caseHeader?.caseInfo?.yearBuiltIn
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getCaseHoldStatus(controlNumber) {
      dispatch(getCaseHoldStatus(controlNumber));
    }
  };
};

OrderInformation.defaultProps = {
  policyNumber: stringFieldDefault,
  trackingNumber: stringFieldDefault,
  loanNumber: stringFieldDefault,
  reportType: stringFieldDefault,
  assignmentType: null,
  customerName: stringFieldDefault,
  orderedDate: stringFieldDefault,
  surveyDate: stringFieldDefault,
  returnedDate: stringFieldDefault,
  completedDate: stringFieldDefault,
  caseType: caseTypeDefault,
  customerDueDate: stringFieldDefault,
  showAsTwoColumn: false,
  governmentSponsor: stringFieldDefault,
};

OrderInformation.propTypes = {
  controlNumber: PropTypes.number,
  policyOrLoanNumber: PropTypes.string,
  trackingNumber: PropTypes.string,
  reportType: PropTypes.string,
  originalSurveyType: PropTypes.string,
  assignmentType: PropTypes.string,
  customerNumber: PropTypes.string,
  customerName: PropTypes.string,
  orderedDate: PropTypes.string,
  surveyDate: PropTypes.string,
  returnedDate: PropTypes.string,
  completedDate: PropTypes.string,
  primaryStatus: PropTypes.string,
  webStatus: PropTypes.string,
  AppraisalStatus: PropTypes.string,
  customerDueDate: PropTypes.string,
  orderCodeOut: PropTypes.string,
  fetchingHoldStatus: PropTypes.bool,
  holdStatus: PropTypes.bool,
  getCaseHoldStatus: PropTypes.func,
  governmentSponsor: PropTypes.string,
  yearBuiltIn: PropTypes.string,
  coverageAIn: PropTypes.number,
  coverageAOut: PropTypes.number,
  percentITV: PropTypes.number,
  channel: PropTypes.string,
  className: PropTypes.string,
  caseType: PropTypes.string,
  user: PropTypes.object, //Being used to hide customer due date from apparisers
  showAsTwoColumn: PropTypes.bool, //Set to true to show date fields in a second column,
  legacyDataEntryPageLink: PropTypes.string,
  stampsAndPhotoRequirementsLink: PropTypes.object,
  cubiCasaStatus: PropTypes.string,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OrderInformation));
